import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarClock } from "lucide-react";
import { api, errText } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { groupByDay, timeOnly } from "../../lib/slots";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const KIND_CLS = { visit: "bg-sky-100 text-sky-700", pickup: "bg-emerald-100 text-emerald-700" };

const SlotForm = ({ litterId, onAdded, t }) => {
  const [f, setF] = useState({ kind: "visit", start: "", duration_min: "30", count: "1", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const add = async () => {
    if (!f.start) return;
    setBusy(true);
    try {
      await api.post(`/my/litters/${litterId}/slots`, { ...f, start: new Date(f.start).toISOString(), duration_min: Number(f.duration_min), count: Number(f.count) || 1 });
      toast.success(t("sl_added")); onAdded();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-slate-50 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end" data-testid="slot-form">
      <Select value={f.kind} onValueChange={set("kind")}>
        <SelectTrigger className="rounded-lg h-9 bg-white" data-testid="slot-kind"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="visit">{t("sl_visit")}</SelectItem><SelectItem value="pickup">{t("sl_pickup")}</SelectItem></SelectContent>
      </Select>
      <Input type="datetime-local" value={f.start} onChange={(e) => set("start")(e.target.value)} data-testid="slot-start" className="rounded-lg h-9 bg-white col-span-1 sm:col-span-2" aria-label={t("sl_start")} />
      <Select value={f.duration_min} onValueChange={set("duration_min")}>
        <SelectTrigger className="rounded-lg h-9 bg-white" data-testid="slot-length"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{["15", "30", "45", "60", "90", "120"].map((m) => <SelectItem key={m} value={m}>{m} {t("sl_min")}</SelectItem>)}</SelectContent>
      </Select>
      <label className="text-[11px] text-slate-500 col-span-1">{t("sl_count")}
        <Input type="number" min={1} max={12} value={f.count} onChange={(e) => set("count")(e.target.value)} data-testid="slot-count" className="rounded-lg h-9 bg-white mt-0.5" />
      </label>
      <Input value={f.note} onChange={(e) => set("note")(e.target.value)} maxLength={200} placeholder={t("sl_note_ph")} data-testid="slot-note" className="rounded-lg h-9 bg-white col-span-2" />
      <Button onClick={add} disabled={busy || !f.start} data-testid="slot-add" className="rounded-lg h-9 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1"><Plus size={14} /> {t("sl_add")}</Button>
    </div>
  );
};

export function SlotsManager({ litter, onClose }) {
  const { t, lang } = useLanguage();
  const [slots, setSlots] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const load = () => litter && api.get(`/my/litters/${litter.id}/slots`).then((r) => setSlots(r.data)).catch(() => {});
  useEffect(() => { load(); }, [litter]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (id) => { try { await api.delete(`/my/litters/${litter.id}/slots/${id}`); load(); } catch (e) { toast.error(errText(e)); } };
  return (
    <Dialog open={!!litter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="slots-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><CalendarClock size={18} className="text-[#F5551E]" /> {t("sl_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("sl_desc")}</DialogDescription>
        </DialogHeader>
        {litter && <SlotForm litterId={litter.id} onAdded={load} t={t} />}
        {slots.length === 0 ? <p className="text-xs text-slate-400">{t("sl_none")}</p> : groupByDay(slots, lang).map((g) => (
          <div key={g.day}>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">{g.day}</p>
            <div className="space-y-1.5">
              {g.items.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl border border-slate-200/80 px-3 py-2" data-testid={`slot-${s.id}`}>
                  <span className="text-sm font-bold text-[#12234A] w-20">{timeOnly(s.start, lang)}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${KIND_CLS[s.kind]}`}>{t(`sl_${s.kind}`)}</span>
                  <span className="text-[11px] text-slate-400">{s.duration_min} {t("sl_min")}</span>
                  <span className="flex-1 min-w-0 text-xs truncate" data-testid={`slot-status-${s.id}`}>
                    {s.booked_by ? <><span className="text-slate-500">{t("sl_booked_by")}</span> <strong className="text-[#12234A]">{s.booked_name}</strong> <a href={`mailto:${s.booked_email}`} className="text-[#F5551E]">{s.booked_email}</a></> : <span className="text-emerald-600 font-semibold">{t("sl_open")}</span>}
                  </span>
                  {s.booked_by && confirmId !== s.id
                    ? <button onClick={() => setConfirmId(s.id)} aria-label={t("sl_delete")} data-testid={`slot-delete-${s.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    : confirmId === s.id
                      ? <button onClick={() => { setConfirmId(null); del(s.id); }} data-testid={`slot-delete-confirm-${s.id}`} className="text-[11px] font-bold text-red-600">{t("sl_del_q")}</button>
                      : <button onClick={() => del(s.id)} aria-label={t("sl_delete")} data-testid={`slot-delete-${s.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
